// Local-first persistence. The game always works offline via localStorage; the net/ layer
// optionally syncs this same SaveState to the backend when the player is logged in.

import { DEFAULT_SETTINGS, type SaveState, type Settings } from '@shared/types';

const SAVE_KEY = 'ansoku.save.v1';
const SETTINGS_KEY = 'ansoku.settings.v1';

export function saveState(state: SaveState): void {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(state));
  } catch {
    /* storage full / unavailable — ignore, game still runs in-memory */
  }
}

export function loadState(): SaveState | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SaveState;
    if (!parsed || !Array.isArray(parsed.board) || parsed.board.length !== 100) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearState(): void {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch {
    /* ignore */
  }
}

/** True if there's a resumable game (in progress and not over). */
export function hasResumableGame(): boolean {
  const s = loadState();
  return !!s && s.gameInProgress && !s.gameOver;
}

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<Settings>) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* ignore */
  }
}
